import { RouterOutputs, useTRPC } from '@/trpc'
import Dropdown from '@tandem/ui/Dropdown'
import Errors from '@tandem/ui/Errors'
import { TextInput } from '@tandem/ui/form'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm, useStore } from '@tanstack/react-form'
import clsx from 'clsx'
import { DotIcon, FolderDotIcon, FolderIcon, FolderOpenIcon, InfoIcon, PencilIcon, TrashIcon } from 'lucide-react'
import EllipsisIcon from '@tandem/ui/icons/ellipsis.svg?react'
import { DragEvent, useEffect, useMemo, useRef, useState } from 'react'
import $type from '@tandem/ui/type.css'
import $layout from '@tandem/ui/layout.css'
import $dropdownSelect from '@tandem/ui/DropdownSelect/styles.css'
import { times } from 'lodash'
import { useSidecar } from '@/layouts/Main'
import AttributeListSidecar from '@/catalog/AttributeListSidecar'
import CategoryAttributesSidecar from '@/catalog/CategoryAttributesSidecar'
import $ from './-styles.css'
import { createFileRoute, Link } from '@tanstack/react-router'

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9а-яё]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .replace(/а/g, 'a')
    .replace(/б/g, 'b')
    .replace(/в/g, 'v')
    .replace(/г/g, 'g')
    .replace(/д/g, 'd')
    .replace(/е/g, 'e')
    .replace(/ё/g, 'yo')
    .replace(/ж/g, 'zh')
    .replace(/з/g, 'z')
    .replace(/и/g, 'i')
    .replace(/й/g, 'y')
    .replace(/к/g, 'k')
    .replace(/л/g, 'l')
    .replace(/м/g, 'm')
    .replace(/н/g, 'n')
    .replace(/о/g, 'o')
    .replace(/п/g, 'p')
    .replace(/р/g, 'r')
    .replace(/с/g, 's')
    .replace(/т/g, 't')
    .replace(/у/g, 'u')
    .replace(/ф/g, 'f')
    .replace(/х/g, 'kh')
    .replace(/ц/g, 'ts')
    .replace(/ч/g, 'ch')
    .replace(/ш/g, 'sh')
    .replace(/щ/g, 'sch')
    .replace(/ъ/g, '')
    .replace(/ы/g, 'y')
    .replace(/ь/g, '')
    .replace(/э/g, 'e')
    .replace(/ю/g, 'yu')
    .replace(/я/g, 'ya')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export const Route = createFileRoute('/catalog/taxonomy')({ component: TaxonomyPage })

function TaxonomyPage() {
  let trpc = useTRPC()
  let queryClient = useQueryClient()
  let { data: categories = [], isFetching } = useQuery(trpc.catalog.category.list.queryOptions())

  let tree = useMemo(() => buildTree(categories), [categories])

  let [editingId, setEditingId] = useState<number | null>(null)
  let [draggedId, setDraggedId] = useState<number | null>(null)
  let [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null)

  let selectedCategory = useMemo(
    () => categories.find((c) => c.id === selectedCategoryId),
    [categories, selectedCategoryId]
  )

  useSidecar(
    () =>
      selectedCategoryId && selectedCategory ?
        <CategoryAttributesSidecar
          categoryId={selectedCategoryId}
          categoryName={selectedCategory.name}
          onClose={() => setSelectedCategoryId(null)}
        />
      : <AttributeListSidecar />,
    [selectedCategoryId, selectedCategory]
  )

  let invalidate = () => queryClient.invalidateQueries({ queryKey: trpc.catalog.category.list.queryKey() })

  let deletion = useMutation(
    trpc.catalog.category.delete.mutationOptions({
      onSuccess: invalidate
    })
  )

  let reorder = useMutation(
    trpc.catalog.category.reorder.mutationOptions({
      onSuccess: invalidate
    })
  )

  return (
    <div style={{width: '100%'}}>
      <div class={clsx($type.breadcrumb, $type.small)}>
        <Link to='/catalog' class={$type.underline}>
          Каталог
        </Link>
        <span>/</span>
        <b>Таксономия</b>
      </div>

      {/*<h2>Дерево категорий</h2>*/}

      <br />
      <br />
      <div class={$.grid} style={{ gridTemplateColumns: '1fr auto' }}>
        <div class={$.gridHeader}>
          <div>Категория</div>
          {/*<div>
            <div class={$layout.row}>
              Слаг
              <span title='Часть URL-адреса.'>
                <InfoIcon color='gray' />
              </span>
            </div>
          </div>*/}
          <div></div>
        </div>
        {tree.map((node, i) => (
          <TreeNode
            key={node.id}
            node={node}
            editingId={editingId}
            setEditingId={setEditingId}
            deletion={deletion}
            draggedId={draggedId}
            setDraggedId={setDraggedId}
            reorder={reorder}
            siblingIndex={i}
            siblings={tree}
            selectedCategoryId={selectedCategoryId}
            onSelectCategory={setSelectedCategoryId}
          />
        ))}
        <EditRow parentId={null} />
      </div>
    </div>
  )
}

type Category = RouterOutputs['catalog']['category']['list'][number]

type TreeNode = Category & { children: TreeNode[] }

type TreeNodeProps = {
  node: TreeNode
  level?: number
  editingId: number | null
  setEditingId: (id: number | null) => void
  deletion: ReturnType<typeof useMutation<any, any, any>>
  draggedId: number | null
  setDraggedId: (id: number | null) => void
  reorder: ReturnType<typeof useMutation<any, any, any>>
  siblingIndex: number
  siblings: TreeNode[]
  selectedCategoryId: number | null
  onSelectCategory: (id: number | null) => void
}

function TreeNode({
  node,
  level = 0,
  editingId,
  setEditingId,
  deletion,
  draggedId,
  setDraggedId,
  reorder,
  siblingIndex,
  siblings,
  selectedCategoryId,
  onSelectCategory
}: TreeNodeProps) {
  let [isExpanded, setIsExpanded] = useState(false)
  let [isDropTarget, setIsDropTarget] = useState(false)
  let isEditing = editingId === node.id
  let isDragging = draggedId === node.id
  let isSelected = selectedCategoryId === node.id

  return (
    <>
      {siblingIndex === 0 && (
        <DropZone
          parentId={node.parent_id}
          visualOrder={0}
          siblings={siblings}
          draggedId={draggedId}
          reorder={reorder}
        />
      )}
      {isEditing ?
        <EditRow node={node} level={level} onDone={() => setEditingId(null)} />
      : <div
          class={clsx(
            $.gridRow,
            $.draggable,
            isDragging && $.dragging,
            isDropTarget && $.dropTarget,
            isSelected && $.selected
          )}
          draggable={true}
          onDragStart={(e: DragEvent) => {
            setDraggedId(node.id)
            e.dataTransfer.effectAllowed = 'move'
            e.dataTransfer.setData('text/plain', String(node.id))
          }}
          onDragEnd={() => setDraggedId(null)}
          onDragOver={(e: DragEvent) => {
            if (draggedId && draggedId !== node.id) {
              e.preventDefault()
              e.dataTransfer.dropEffect = 'move'
              setIsDropTarget(true)
            }
          }}
          onDragLeave={() => setIsDropTarget(false)}
          onDrop={(e: DragEvent) => {
            e.preventDefault()
            setIsDropTarget(false)
            let id = Number(e.dataTransfer.getData('text/plain'))
            if (id !== node.id) {
              reorder.mutate({ id, parent_id: node.id, order: node.children.length })
              setIsExpanded(true)
            }
          }}
        >
          <div>
            <button
              class={clsx('plain', $type.underline)}
              onClick={() => onSelectCategory(isSelected ? null : node.id)}
            >
              {times(level, () => (
                <DotIcon />
              ))}
              <span
                onClick={(e) => {
                  e.stopPropagation()
                  setIsExpanded((v) => !v)
                }}
                style={{ display: 'flex' }}
              >
                {isExpanded ?
                  <FolderOpenIcon />
                : node.children.length > 0 ?
                  <FolderDotIcon />
                : <FolderIcon />}
              </span>
              {node.name}
            </button>
          </div>
          <div>
            <Dropdown
              trigger={
                <button class={clsx('plain', $type.muted)} title='Действия'>
                  <EllipsisIcon style={{ width: '1em' }} />
                </button>
              }
            >
              <div class={$dropdownSelect.menu}>
                <button class={clsx('ghost', $dropdownSelect.option)} onClick={() => setEditingId(node.id)}>
                  <PencilIcon />
                  Редактировать
                </button>
                <button
                  class={clsx('ghost', $dropdownSelect.option)}
                  onClick={() => {
                    if (confirm(`Удалить категорию «${node.name}»?`)) {
                      deletion.mutate({ id: node.id })
                    }
                  }}
                >
                  <TrashIcon />
                  Удалить
                </button>
              </div>
            </Dropdown>
          </div>
        </div>
      }
      {isExpanded && (
        <>
          {node.children.map((child, i) => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              editingId={editingId}
              setEditingId={setEditingId}
              deletion={deletion}
              draggedId={draggedId}
              setDraggedId={setDraggedId}
              reorder={reorder}
              siblingIndex={i}
              siblings={node.children}
              selectedCategoryId={selectedCategoryId}
              onSelectCategory={onSelectCategory}
            />
          ))}
          <EditRow parentId={node.id} level={level + 1} />
        </>
      )}
      <DropZone
        parentId={node.parent_id}
        visualOrder={siblingIndex + 1}
        siblings={siblings}
        draggedId={draggedId}
        reorder={reorder}
      />
    </>
  )
}

function DropZone({
  parentId,
  visualOrder,
  siblings,
  draggedId,
  reorder
}: {
  parentId: number | null
  visualOrder: number
  siblings: TreeNode[]
  draggedId: number | null
  reorder: ReturnType<typeof useMutation<any, any, any>>
}) {
  let [isOver, setIsOver] = useState(false)

  // Compute the insertion index in the list after removing the dragged item
  let draggedIndex = draggedId !== null ? siblings.findIndex((s) => s.id === draggedId) : -1

  // Hide drop zones directly above and below the dragged item (they're no-ops)
  let isAdjacentToDragged = draggedIndex !== -1 && (visualOrder === draggedIndex || visualOrder === draggedIndex + 1)
  let hidden = draggedId === null || isAdjacentToDragged

  let insertOrder = visualOrder
  if (draggedIndex !== -1 && draggedIndex < visualOrder) {
    insertOrder = visualOrder - 1
  }

  return (
    <div
      class={clsx($.dropZone, !hidden && isOver && $.dropZoneActive)}
      style={hidden ? { pointerEvents: 'none' } : undefined}
      onDragOver={
        hidden ? undefined : (
          (e: DragEvent) => {
            e.preventDefault()
            e.dataTransfer.dropEffect = 'move'
            setIsOver(true)
          }
        )
      }
      onDragLeave={hidden ? undefined : () => setIsOver(false)}
      onDrop={
        hidden ? undefined : (
          (e: DragEvent) => {
            e.preventDefault()
            setIsOver(false)
            let id = Number(e.dataTransfer.getData('text/plain'))
            reorder.mutate({ id, parent_id: parentId, order: insertOrder })
          }
        )
      }
    />
  )
}

function EditRow({
  node,
  parentId,
  level = 0,
  onDone
}: {
  node?: TreeNode
  parentId?: number | null
  level?: number
  onDone?: () => void
}) {
  let trpc = useTRPC()
  let queryClient = useQueryClient()
  let slugTouched = useRef(false)
  let isNew = !node

  let mutation = useMutation({
    ...(isNew ? trpc.catalog.category.create.mutationOptions() : trpc.catalog.category.update.mutationOptions()),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: trpc.catalog.category.list.queryKey() })
  })

  let form = useForm({
    defaultValues: {
      name: node?.name ?? '',
      slug: node?.slug ?? '',
      parent_id: node?.parent_id ?? parentId ?? null
    },
    validators: {
      async onSubmitAsync({ value }) {
        if (isNew) {
          await (mutation as ReturnType<typeof useMutation<any, any, any>>).mutateAsync(value)
          form.reset()
          slugTouched.current = false
        } else {
          await (mutation as ReturnType<typeof useMutation<any, any, any>>).mutateAsync({ ...value, id: node!.id })
          onDone?.()
        }
      }
    }
  })

  let name = useStore(form.store, (s) => s.values.name)

  return (
    <div class={$.gridRow}>
      <div>
        <div class={$layout.row} style={{ width: '100%' }}>
          {times(level, () => (
            <DotIcon />
          ))}
          {!isNew && <FolderOpenIcon />}
          <form.Field name='name'>
            {(field) => (
              <TextInput
                field={field}
                placeholder={
                  isNew ?
                    parentId != null ?
                      'Название'
                    : 'Новая категория'
                  : undefined
                }
                style={{ flex: 1 }}
                onValueChange={(val) => {
                  if (!slugTouched.current) {
                    form.setFieldValue('slug', slugify(val))
                  }
                }}
              />
            )}
          </form.Field>
        </div>
        <form.Field
          name='slug'
          validators={{
            onChange: ({ value }) => {
              if (!value) return isNew ? undefined : 'Обязательное поле'
              if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value)) return 'Латиница, цифры, дефис'
            }
          }}
        >
          {(field) => (
            <div>
              <div class={$layout.row}>
                <TextInput
                  field={field}
                  placeholder={isNew ? 'Слаг' : undefined}
                  style={{ flex: 1 }}
                  onValueChange={() => {
                    slugTouched.current = true
                  }}
                />
              </div>
              <Errors errors={field.state.meta.errors as any} />
            </div>
          )}
        </form.Field>
      </div>
      <div class={$layout.row}>
        <button class='secondary' disabled={!name.trim() || mutation.isPending} onClick={() => form.handleSubmit()}>
          {mutation.isPending ?
            isNew ?
              'Создание…'
            : 'Сохранение…'
          : 'Сохранить'}
        </button>
        {!isNew && (
          <button class={clsx('ghost', $type.underline, $type.muted)} onClick={onDone}>
            Отменить
          </button>
        )}
      </div>
    </div>
  )
}

function buildTree(categories: Category[]): TreeNode[] {
  let map = new Map<number, TreeNode>()
  let roots: TreeNode[] = []

  for (let cat of categories) {
    map.set(cat.id, { ...cat, children: [] })
  }

  for (let cat of categories) {
    let node = map.get(cat.id)!
    if (cat.parent_id && map.has(cat.parent_id)) {
      map.get(cat.parent_id)!.children.push(node)
    } else {
      roots.push(node)
    }
  }

  return roots
}
